import { useContext, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { maskWholeNumber, pluralize } from '@givebutter/util';
import { createBulkAction } from 'api/bulkActions';
import { useContactTypeContext } from 'assets/js/pages/contacts/ContactTypeContext';
import * as DropdownMenu from 'components/dropdown-menu';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { TableContext } from 'components/table/TableContext';
import { BulkActionType } from 'constants/bulkActions';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ArchiveContactsDialogProps
    extends Omit<AlertDialog.AlertDialogRootProps, 'open' | 'children'> {
    isArchived: boolean;
}

const ArchiveContactsDialog = ({
    isArchived,

    onOpenChange,
    ...rest
}: ArchiveContactsDialogProps) => {
    const toast = useToast();
    const contactType = useContactTypeContext();
    const filterKey = contactType === ContactType.Individual ? 'contacts' : 'companies';
    const [isOpen, setIsOpen] = useState(false);
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { table, searchTerm } = useContext(TableContext);
    const tableState = table.getState();
    const { filters } = useFiltersByKey(filterKey);
    const isAllSelected = tableState.isAllRecordsSelected;
    const selectedRowIds = Object.keys(tableState.rowSelection).map(Number);
    const contactIds: number[] = isAllSelected ? [] : selectedRowIds;
    const rowCount = isAllSelected ? table.getRowCount() : contactIds.length;

    const { mutate } = useMutation({
        mutationFn: () => {
            return createBulkAction(accountId, {
                type: isArchived
                    ? BulkActionType.UNARCHIVE_CONTACTS
                    : BulkActionType.ARCHIVE_CONTACTS,
                filters: isAllSelected
                    ? { query: searchTerm, type: contactType, ...filters }
                    : undefined,
                data: { ids: isAllSelected ? undefined : contactIds },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
            });
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.bulkActions(accountId) });

            toast.success(`${isArchived ? 'Unarchive' : 'Archive'} action queued successfully`);
            table.toggleAllRowsSelected?.(false);
            setIsOpen(false);
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message ?? 'Unknown error occurred');
        },
    });

    return (
        <AlertDialog.Root
            open={isOpen}
            onOpenChange={isOpen => {
                setIsOpen(isOpen);
                onOpenChange?.(isOpen);
            }}
            {...rest}
        >
            <AlertDialog.Trigger asChild onClick={() => setIsOpen(true)}>
                <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                    {isArchived ? 'Unarchive' : 'Archive'}
                </DropdownMenu.Item>
            </AlertDialog.Trigger>
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content className="max-w-lg">
                    <AlertDialog.Close />
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                    </AlertDialog.Header>

                    <AlertDialog.Body className="gap-4">
                        <AlertDialog.Title>
                            {isArchived ? 'Unarchive Contacts' : 'Archive Contacts'}
                        </AlertDialog.Title>
                        <AlertDialog.Description>
                            <AlertNotification.Root type={isAllSelected ? 'warning' : 'info'}>
                                <AlertNotification.Icon />
                                <AlertNotification.Content>
                                    <AlertNotification.Title>
                                        You are about to {isArchived ? 'unarchive' : 'archive'}{' '}
                                        {maskWholeNumber(rowCount)} {pluralize(rowCount, 'contact')}
                                        {isAllSelected ? ' across multiple pages.' : '.'}
                                    </AlertNotification.Title>
                                    <AlertNotification.Description>
                                        Ensure you have properly selected the contacts you would
                                        like to {isArchived ? 'unarchive' : 'archive'} before
                                        continuing.
                                    </AlertNotification.Description>
                                </AlertNotification.Content>
                            </AlertNotification.Root>
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <Button
                            className="flex-1"
                            variant="solid"
                            onClick={() => mutate()}
                            disabled={rowCount === 0}
                        >
                            {isArchived ? 'Unarchive' : 'Archive'} {maskWholeNumber(rowCount)}{' '}
                            {pluralize(rowCount, 'contact')}
                        </Button>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

export { ArchiveContactsDialog };
